import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, User, LogOut, Trophy, MessageSquare, Users, BookOpen, Award, Calendar, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navbar() {
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  }, []);
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  const handleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);
  
  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear localStorage token for Safari compatibility
      localStorage.removeItem('session_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear token even if API call fails
      localStorage.removeItem('session_token');
    }
  }, [navigate]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  
  return (
    <nav className="bg-deep-dark/95 border-b border-premium-gold/20 sticky top-0 z-50 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="flex flex-col items-center">
              <span className="text-3xl md:text-4xl font-black text-white italic transition-all duration-300 group-hover:scale-105" 
                    style={{ fontFamily: 'Fraunces, serif', fontWeight: 900, fontStyle: 'italic', letterSpacing: '0.02em' }}>
                D<span className="text-energy-red">I</span>OS
              </span>
              <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontStyle: 'italic', fontWeight: 900, color: '#C9A84C', fontSize: '16px', letterSpacing: '3px' }}>TAP IN</span>
            </div>
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            <Link 
              to="/courses" 
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                ${isActive('/courses') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
              data-testid="nav-courses-link"
            >
              <BookOpen className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              Courses
            </Link>
            <Link 
              to="/become-coach" 
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                ${isActive('/become-coach') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
              data-testid="nav-become-coach-link"
            >
              <Award className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              Coach
            </Link>
            <Link 
              to="/synergies/facilities" 
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                ${isActive('/synergies/facilities') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
              data-testid="nav-synergies-link"
            >
              <Globe className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              Synergies
            </Link>
            
            {user && (
              <>
                <Link 
                  to="/messages" 
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                    ${isActive('/messages') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
                  data-testid="nav-messages-link"
                >
                  <MessageSquare className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Messages
                </Link>
                <Link 
                  to="/my-bookings" 
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                    ${isActive('/my-bookings') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
                  data-testid="nav-bookings-link"
                >
                  <Calendar className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Bookings
                </Link>
                <Link 
                  to="/referrals" 
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2
                    ${isActive('/referrals') ? 'text-premium-gold bg-dark-surface' : 'text-white/80 hover:text-white hover:bg-dark-surface/50'}`}
                  data-testid="nav-referrals-link"
                >
                  <Users className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Referrals
                </Link>
              </>
            )}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-2 ml-4 px-3 py-1.5 rounded-lg bg-dark-surface hover:bg-dark-surface/80 transition-all duration-200 border border-premium-gold/30" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-premium-gold" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-premium-gold flex items-center justify-center">
                        <User className="w-5 h-5 text-deep-dark" />
                      </div>
                    )}
                    <span className="font-semibold text-white">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-dark-surface border border-premium-gold/30 rounded-lg shadow-xl">
                  <DropdownMenuItem asChild>
                    <Link to={user.role === 'coach' ? '/coach/dashboard' : '/dashboard'} className="cursor-pointer text-white hover:text-premium-gold hover:bg-deep-dark/50 rounded px-3 py-2 flex items-center gap-2" data-testid="nav-dashboard-link">
                      <Trophy className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-white hover:text-premium-gold hover:bg-deep-dark/50 rounded px-3 py-2 flex items-center gap-2" data-testid="logout-button">
                    <LogOut className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={handleLogin}
                className="ml-4 bg-premium-gold hover:opacity-90 text-deep-dark font-bold tracking-wider px-6 py-2.5 rounded-lg transition-all duration-300 hover:shadow-glow-gold"
                data-testid="login-button"
              >
                Login
              </button>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-premium-gold p-2 rounded-lg hover:bg-dark-surface transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-dark-surface border-t border-premium-gold/20" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            <Link 
              to="/courses" 
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold text-sm transition-colors
                ${isActive('/courses') ? 'text-premium-gold bg-deep-dark/50' : 'text-white/80 hover:text-white hover:bg-deep-dark/30'}`}
              data-testid="mobile-nav-courses-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <BookOpen className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              Browse Courses
            </Link>
            <Link 
              to="/become-coach" 
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold text-sm transition-colors
                ${isActive('/become-coach') ? 'text-premium-gold bg-deep-dark/50' : 'text-white/80 hover:text-white hover:bg-deep-dark/30'}`}
              data-testid="mobile-nav-become-coach-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Award className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              Become a Coach
            </Link>
            <Link 
              to="/synergies/facilities" 
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold text-sm transition-colors
                ${isActive('/synergies/facilities') ? 'text-premium-gold bg-deep-dark/50' : 'text-white/80 hover:text-white hover:bg-deep-dark/30'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Globe className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
              DIOS Synergies
            </Link>
            {user ? (
              <>
                <Link 
                  to="/messages" 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-deep-dark/30 font-semibold text-sm transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <MessageSquare className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Messages
                </Link>
                <Link 
                  to="/my-bookings" 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-deep-dark/30 font-semibold text-sm transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Calendar className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Bookings
                </Link>
                <Link 
                  to="/referrals" 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-deep-dark/30 font-semibold text-sm transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Users className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Referrals
                </Link>
                <Link 
                  to={user.role === 'coach' ? '/coach/dashboard' : '/dashboard'} 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:text-white hover:bg-deep-dark/30 font-semibold text-sm transition-colors"
                  data-testid="mobile-nav-dashboard-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Trophy className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Dashboard
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-white/80 hover:text-premium-gold hover:bg-deep-dark/30 font-semibold text-sm transition-colors"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-5 h-5 text-premium-gold" strokeWidth={1.5} />
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className="w-full bg-premium-gold hover:opacity-90 text-deep-dark font-bold tracking-wider px-6 py-3 rounded-lg transition-all mt-4"
                data-testid="mobile-login-button"
              >
                Login
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
